import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { RoutingProfile } from "@repo/core/models"

import { AgentProfile } from "@/agent-state/use-fetch-agent-profile"
import { Button } from "@/components/ui/button"
import {
  DialogBody,
  DialogBodyError,
  DialogBodySuccess,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form"
import { formatFullName } from "@/helpers/names"
import { RoutingProfilePicker } from "@/pages/Users/edit/routing-profile-picker"
import { useMutationUpdateUser } from "@/pages/Users/users-api"

type Props = {
  agentProfile: AgentProfile
  close: (value: boolean) => void
  routingProfiles: RoutingProfile[]
}

type FormValues = {
  routingProfileId: string
}

export function DialogEditProfile({
  agentProfile,
  close,
  routingProfiles,
}: Props) {
  const { t } = useTranslation()
  const mutation = useMutationUpdateUser(agentProfile.cognito_user_id)
  const routingProfileId = agentProfile.routing_profile_id ?? ""

  const form = useForm<FormValues>({
    mode: "onBlur",
    reValidateMode: "onChange",
    values: {
      routingProfileId,
    },
  })

  const hasChanged = routingProfileId !== form.watch("routingProfileId")

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault()
    const values = form.getValues()

    if (!values.routingProfileId) {
      return
    }

    await mutation.mutateAsync(values.routingProfileId)
  }

  return (
    <DialogContent isPending={mutation.isPending} className="max-w-2xl">
      <form onSubmit={handleSave}>
        <DialogHeader>
          <DialogTitle>{t("header.editProfile.title")}</DialogTitle>
        </DialogHeader>
        {(mutation.isIdle || mutation.isPending) && (
          <>
            <DialogBody>
              <ProfileForm
                form={form}
                agentProfile={agentProfile}
                routingProfiles={routingProfiles}
              />
            </DialogBody>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => close(false)}
                isDisabled={mutation.isPending}
              >
                {t("global.button.cancel")}
              </Button>
              <Button
                type="submit"
                isLoading={mutation.isPending}
                isDisabled={!hasChanged}
              >
                {t("global.button.save")}
              </Button>
            </DialogFooter>
          </>
        )}
        {mutation.isSuccess && (
          <>
            <DialogBodySuccess>
              {t("users.edit.success", {
                name: formatFullName(
                  agentProfile.identity_info.first_name,
                  agentProfile.identity_info.last_name,
                ),
              })}
            </DialogBodySuccess>
            <DialogFooter>
              <Button variant="outline" onClick={() => close(true)}>
                {t("global.button.close")}
              </Button>
            </DialogFooter>
          </>
        )}
        {mutation.isError && (
          <>
            <DialogBodyError>{t("header.editProfile.error")}</DialogBodyError>
            <DialogFooter>
              <Button variant="outline" onClick={() => close(false)}>
                {t("global.button.close")}
              </Button>
            </DialogFooter>
          </>
        )}
      </form>
    </DialogContent>
  )
}

function ProfileForm({
  agentProfile,
  form,
  routingProfiles,
}: {
  agentProfile: AgentProfile
  form: ReturnType<typeof useForm<FormValues>>
  routingProfiles: RoutingProfile[]
}) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-6">
      <Form {...form}>
        <FormItem>
          <FormLabel className="text-muted-foreground">
            {t("users.list.name")}
          </FormLabel>
          <div>
            {formatFullName(
              agentProfile.identity_info.first_name,
              agentProfile.identity_info.last_name,
            )}
          </div>
        </FormItem>

        <FormItem>
          <FormLabel className="text-muted-foreground">
            {t("users.list.role")}
          </FormLabel>
          <div>
            {agentProfile.security_profile_names
              .map((name) =>
                t("header." + name.charAt(0).toLowerCase() + name.slice(1)),
              )
              .join(", ")}
          </div>
        </FormItem>

        <FormItem>
          <FormLabel className="text-muted-foreground">
            {t("users.list.email")}
          </FormLabel>
          <div>{agentProfile.email}</div>
        </FormItem>

        <FormField
          control={form.control}
          name="routingProfileId"
          label={t("users.edit.routingProfile")}
          render={({ field }) => (
            <FormItem className="flex flex-col gap-2">
              <FormLabel className="text-muted-foreground">
                {t("users.edit.routingProfile")}
              </FormLabel>
              <FormControl>
                <RoutingProfilePicker
                  {...field}
                  routingProfiles={routingProfiles}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </Form>
    </div>
  )
}
